import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import { withModal } from 'components/modal/ModalHOC';
import ActionButton from 'components/buttons/action-button/ActionButton';
import PromptModal from 'components/modal/prompt-modal/PromptModal';

import { BlockTitle } from '../../admin-panel';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { fetchRealmOutfitsData, resetRealmOutfitsData } from 'modules/realm-outfits/reducers/admin-realm-outfit-data';
import {
  disableOutfitSection,
  enableOutfitSection,
  removeEmsMapping
} from 'modules/realm-outfits/reducers/realm-outfits';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerRealm } from 'utils/account';

import './profile-outfit-panel.scss';

import type { InfluencerProfile, State } from 'types';

applyTranslations(require('./profile-outfit-panel.i18n.json'));

type OwnProps = {
  profile: InfluencerProfile,
  onChange: (a: void) => void
};

type StateProps = {
  outfitCreatorId: number | null,
  isOutfitDataLoading: boolean,
  isSCP: boolean
};

type DispatchProps = {
  dispatchEnableOutfitSection: (
    influencerId: number,
    emInfluencerUuid: string,
    isSCP: boolean
  ) => Promise<void>,
  dispatchDisableOutfitSection: (influencerId: number) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      title?: string,
      onConfirm?: () => void,
      onCancel?: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchFetchRealmOutfitsData: (influencerId: number) => Promise<void>,
  dispatchResetRealmOutfitsData: () => Promise<void>,
  dispatchRemoveEmsMapping: (influencerId: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const MapAndRemapActionButtonWithModal = withModal(ActionButton, {
  modalContent: PromptModal
});

class ProfileOutfitPanel extends React.Component<Props> {
  static defaultProps = {
    onChange: () => {}
  };

  componentDidMount() {
    const {
      profile,
      dispatchFetchRealmOutfitsData
    } = this.props;

    dispatchFetchRealmOutfitsData(profile.id);
  }

  componentDidUpdate(prevProps: Props) {
    const { profile, dispatchFetchRealmOutfitsData } = this.props;
    if (profile.id !== prevProps.profile.id) {
      dispatchFetchRealmOutfitsData(profile.id);
    }
  }

  componentWillUnmount() {
    this.props.dispatchResetRealmOutfitsData();
  }

  handleEnableRealm = (emInfluencerUuid: string): void => {
    const {
      profile,
      isSCP,
      dispatchEnableOutfitSection,
      onChange
    } = this.props;

    dispatchEnableOutfitSection(profile.id, emInfluencerUuid, isSCP)
      .then(onChange);
  };

  handleRemoveMapping = (): void => {
    const {
      profile,
      dispatchRemoveEmsMapping,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;

    dispatchShowConfirmationModal({
      title: __t('profile_outfit_panel.unmap_ems_profile_title'),
      message: __t('profile_outfit_panel.unmap_ems_profile_message'),
      onConfirm: () => dispatchRemoveEmsMapping(profile.id)
        .then(() => dispatchHideConfirmationModal())
        .then(() => window.location.reload()),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  handleDisableRealm = (): void => {
    const {
      profile,
      onChange,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchDisableOutfitSection
    } = this.props;

    dispatchShowConfirmationModal({
      message: __t('profile_outfit_panel.enable_status_confirmation'),
      onConfirm: () =>
        dispatchDisableOutfitSection(profile.id)
          .then(onChange)
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () =>
        dispatchHideConfirmationModal()
    });
  };

  renderMappedStatus = (): React.ReactNode => {
    const { outfitCreatorId } = this.props;

    const statusClasses = classnames('profile-approval-panel__block-status', {
      'profile-approval-panel__block-status--not-mapped': isNil(outfitCreatorId)
    });

    return (
      <div className={statusClasses}>
        {
          !isNil(outfitCreatorId)
            ? __t('profile_outfit_panel.mapped')
            : __t('profile_outfit_panel.not_mapped')
        }
      </div>
    );
  };

  renderSCPButton = (): React.ReactNode => {
    const {
      outfitCreatorId,
      isOutfitDataLoading
    } = this.props;
    const mapAndRemapButtonText = isNil(outfitCreatorId)
      ? __t('profile_outfit_panel.map')
      : __t('profile_outfit_panel.remap');

    return (
      <React.Fragment>
        <MapAndRemapActionButtonWithModal
          className="profile-outfit-panel__button"
          disabled={isOutfitDataLoading}
          modalContentProps={{
            onSubmit: (emInfluencerUuid) => this.handleEnableRealm(emInfluencerUuid),
            title: __t('profile_outfit_panel.em_profile_modal.scp.title', { text: mapAndRemapButtonText }),
            label: __t('profile_outfit_panel.em_profile_modal.scp.label'),
            value: '',
            okBtnText: mapAndRemapButtonText
          }}
        >
          {mapAndRemapButtonText}
        </MapAndRemapActionButtonWithModal>
        {!isNil(outfitCreatorId) ? (
          <ActionButton
            className="profile-outfit-panel__button"
            onClick={this.handleRemoveMapping}
          >
            {__t('profile_outfit_panel.unmap')}
          </ActionButton>
        ) : null}
      </React.Fragment>
    );
  };

  render() {
    const { isSCP } = this.props;

    if (!isSCP) {
      return null;
    }

    return (
      <section>
        <div className="profile-approval-panel__block">
          <BlockTitle title={`${__t('profile_outfit_panel.title_scp')}:`}>
            {this.renderMappedStatus()}
          </BlockTitle>
        </div>
        <div className="profile-approval-panel__actions">
          {this.renderSCPButton()}
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ account, adminRealmOutfitData, runtimeConfig }: State): StateProps => ({
  outfitCreatorId: adminRealmOutfitData.data?.outfitCreatorId ?? null,
  isOutfitDataLoading: adminRealmOutfitData.isLoading,
  isSCP: isInfluencerRealm(account.roles)
    && runtimeConfig.config.zalandoOutfitTaskTypeEnabled
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchEnableOutfitSection: (influencerId, emInfluencerUuid, isSCP) => (
    dispatch(enableOutfitSection(influencerId, emInfluencerUuid, isSCP))
  ),
  dispatchDisableOutfitSection: (influencerId) => dispatch(disableOutfitSection(influencerId)),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchFetchRealmOutfitsData: (influencerId: number) => dispatch(fetchRealmOutfitsData(influencerId)),
  dispatchResetRealmOutfitsData: () => dispatch(resetRealmOutfitsData()),
  dispatchRemoveEmsMapping: id => dispatch(removeEmsMapping(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileOutfitPanel);
